"use client";

import { ProductData } from "@/types/product";

interface ProductSuitabilitySectionProps {
  product: ProductData;
}

export default function ProductSuitabilitySection({
  product,
}: ProductSuitabilitySectionProps) {
  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 min-h-[400px] sm:min-h-[500px] rounded-2xl overflow-hidden shadow-lg border border-gray-200">
          {/* Left Column - Product Description & CTA */}
          <div className="bg-[#F6FAED] p-6 sm:p-8 md:p-12 flex flex-col justify-center">
            <div className="max-w-lg">
              {/* Product Title */}
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                {product.productPromo.title || product.productName}
              </h2>

              {/* Description */}
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {product.productPromo.description ? (
                  <div
                    className="text-sm sm:text-base text-gray-700 leading-relaxed prose prose-sm max-w-none rich-text"
                    dangerouslySetInnerHTML={{
                      __html: product.productPromo.description,
                    }}
                  />
                ) : (
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    Complete a short online assessment to unlock personalised
                    treatment choices and select your preferred option and
                    quantity.
                  </p>
                )}
              </div>

              {/* Call to Action Button */}
              <button className="bg-[#7771B8] hover:bg-[#6860a3] text-white px-6 sm:px-8 py-3 md:py-3.5 rounded-lg font-semibold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                {product.productPromo.buttonLabel || "Start weight loss check"}
              </button>
            </div>
          </div>

          {/* Right Column - Suitability Information */}
          <div className="bg-white px-6 sm:px-8 md:px-12 py-6 sm:py-8 md:py-12 flex flex-col justify-start">
            <div className="max-w-lg">
              {/* Dynamic Suitability Items from Playlist */}
              {product.productPromo.playlist.map((item, index) => {
                // Determine styling based on value content
                const isNegative = item.value.toLowerCase().includes("no");
                const isPositive = item.value.toLowerCase().includes("yes");

                let valueStyle = "text-gray-900 font-semibold";
                if (isNegative) {
                  valueStyle =
                    "bg-red-50 text-red-600 px-4 py-1 rounded-full text-sm font-medium";
                } else if (isPositive) {
                  valueStyle =
                    "bg-[#7771B8] text-white px-4 py-1 rounded-full text-sm font-medium";
                }

                return (
                  <div
                    key={index}
                    className="mb-5 border-b border-gray-100 pb-5 last:border-b-0 last:mb-0 last:pb-0"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 font-medium text-sm sm:text-base">
                        {item.label}
                      </span>
                      <span className={valueStyle}>{item.value}</span>
                    </div>
                    {item.info && (
                      <div className="mt-3">
                        <p className="text-gray-600 leading-relaxed text-sm">
                          {item.info}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Fallback content if no playlist items */}
              {(!product.productPromo.playlist ||
                product.productPromo.playlist.length === 0) && (
                <>
                  <div className="mb-5 border-b border-gray-100 pb-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 font-medium text-sm sm:text-base">
                        Suitable for age:
                      </span>
                      <span className="text-gray-900 font-semibold">
                        18 years+
                      </span>
                    </div>
                  </div>

                  <div className="mb-5 border-b border-gray-100 pb-5 last:border-b-0 last:mb-0 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 font-medium text-sm sm:text-base">
                        Consultation required?
                      </span>
                      <span className="bg-[#7771B8] text-white px-4 py-1 rounded-full text-sm font-medium">
                        Yes
                      </span>
                    </div>
                    <div className="mt-3">
                      <p className="text-gray-600 leading-relaxed text-sm">
                        This treatment requires an online consultation for
                        review by our medical team.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
